import {
  selectCorrectionHeight,
  selectCorrectionWidth,
  selectRenderAreaHeight,
} from 'features/layout/features/dimensions/dimensionsSlice';
import { PagedTiles } from 'features/layout/pagination/PagedTiles';
import { useSystemSession } from 'hooks/useSystemSession';
import { useAppSelector } from 'store/hooks';
import { styled } from '@mui/material/styles';
import { usePaginationControls } from 'features/layout/pagination/hooks';
import { PaginationControl } from 'features/layout/pagination/PaginationControl';
import React, { useContext } from 'react';
import { AudioStreams } from 'features/streaming/components/media/AudioStreams';
import { styleConstants } from 'theme/styleConstants';
import { RoomLayoutValuesContext } from 'features/layout/Context';

const Root = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'correctionHeight' &&
    prop !== 'hasPagination' &&
    prop !== 'correctionWidth' &&
    prop !== 'bandHeight',
})<{
  correctionHeight: number;
  correctionWidth: number;
  bandHeight?: number;
  hasPagination: boolean;
}>(({ correctionHeight, correctionWidth, bandHeight, hasPagination }) => {
  const stacked = bandHeight !== undefined;

  return {
    height: stacked ? `${bandHeight}px` : '100%',
    position: 'relative',
    maxHeight: '100%',
    width: '100%',
    transition: stacked ? 'height .05s linear' : 'padding .05s linear',
    padding: `${stacked ? 0 : correctionHeight}px ${
      (hasPagination ? styleConstants.layout.pagination.controlWidth : 0) + correctionWidth
    }px`,
  };
});

export const TileArea = ({
  ref,
  stackedBelowPinned = false,
}: {
  ref?: React.RefObject<HTMLDivElement>;
  stackedBelowPinned?: boolean;
}) => {
  const [canGoNext, canGoPrev] = usePaginationControls();
  const isSystemSession = useSystemSession();

  const hasPagination = !isSystemSession && (canGoNext || canGoPrev);
  const correctionHeight = useAppSelector(selectCorrectionHeight);
  const correctionWidth = useAppSelector(selectCorrectionWidth);
  const renderAreaHeight = useAppSelector(selectRenderAreaHeight);
  const { tilesHeight } = useContext(RoomLayoutValuesContext);

  // The band must end where the tiles end or the gap reopens. Area and correction share one
  // dispatch so it steps with them; the context height caps it while the solver catches up.
  const measuredHeight = (renderAreaHeight || tilesHeight) - correctionHeight * 2;
  const bandHeight = stackedBelowPinned
    ? Math.max(0, Math.min(tilesHeight, measuredHeight))
    : undefined;

  return (
    <Root
      ref={ref}
      id="layout-paged-tiles"
      data-testid="layout-paged-tiles"
      hasPagination={hasPagination}
      correctionHeight={correctionHeight}
      correctionWidth={correctionWidth}
      bandHeight={bandHeight}
    >
      <PagedTiles />
      <AudioStreams />
      {canGoPrev && !isSystemSession && <PaginationControl direction="prev" />}
      {canGoNext && !isSystemSession && <PaginationControl direction="next" />}
    </Root>
  );
};
